import { useQueryClient } from '@tanstack/react-query'
import type { Cube } from '@cubejs-client/core'
import { ArrowLeft, Printer, RefreshCw, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { EditorContent, useEditor } from '@tiptap/react'

import { Alert } from '@/components/feedback/Alert'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Button } from '@/components/ui/button'
import { EditorToolbar } from '@/features/documents/editor/EditorToolbar'
import { createDocumentExtensions } from '@/features/documents/editor/extensions'
import { getDocumentJSON } from '@/features/documents/editor/serialize'
import { DOCUMENT_CLASS } from '@/features/documents/nodes/document-class'
import { FrozenDocumentSource } from '@/features/documents/nodes/frozen-source'
import { Letterhead } from '@/features/documents/print/Letterhead'
import { useCubeMeta } from '@/hooks/use-cube-meta'
import { cubeApi } from '@/lib/cube'
import { formatDateTime } from '@/lib/format'
import { useLeaveGuard } from '@/lib/form/use-leave-guard'
import { messages } from '@/lib/messages'
import { periodLabel } from '@/lib/period'
import { queryKeys } from '@/lib/query-keys'
import { reportDocumentSchema, type ReportDocument } from '@/lib/report-document'
import { cn } from '@/lib/utils'
import { useBiReport } from '@/services/bi/reports'
import {
  isRunVersionConflict,
  useRefreshRun,
  useRun,
  useUpdateRunText,
  type DocumentRun,
} from '@/services/bi/runs'
import { toRows } from '@/services/cube/report'

import { buildRefresh, RunGenerationError } from './generate'
import type { RefreshDiff } from './materialize'
import { RunBlockActions } from './RunBlockActions'
import { RunPdfButton } from './RunPdfButton'
import { RunLifecycleActions, RunRegenerateForm, type LifecycleFailure } from './RunLifecycle'
import { RunRefreshNotice } from './RunRefreshNotice'

const t = messages.documents.runs
const editorText = messages.documents.editor

function RunDocument({
  run,
  template,
  views,
  canEdit,
  onReload,
}: {
  run: DocumentRun
  template: ReportDocument
  views: ReadonlyMap<string, Cube>
  /** Rapor düzenleme yetkisi (kopya yazma uçları aynı yetkiyi ister). */
  canEdit: boolean
  /** Sunucudaki güncel kopyayı çekip editörü baştan kurar (çakışma sonrası). */
  onReload: () => void
}) {
  const editable = canEdit && run.status === 'draft'
  const save = useUpdateRunText(run.report_id, run.id)
  const [, setRevision] = useState(0)
  const [savedVersion, setSavedVersion] = useState<number>()
  const editor = useEditor({
    extensions: createDocumentExtensions(),
    content: run.content,
    editable,
    onUpdate: () => setRevision((revision) => revision + 1),
  })
  // "Verileri yenile" kesin kopyayı draft'a alır; editör yeniden kurulmadan düzenlenebilir olur.
  useEffect(() => {
    editor.setEditable(editable)
  }, [editor, editable])
  const snapshot = JSON.stringify(getDocumentJSON(editor))
  const [saved, setSaved] = useState(snapshot)
  const dirty = editable && snapshot !== saved
  const leaveGuard = useLeaveGuard(dirty)
  const refreshRun = useRefreshRun(run.report_id, run.id)
  const [refresh, setRefresh] = useState<{ busy: boolean; error?: unknown; diff?: RefreshDiff }>({
    busy: false,
  })
  const [regenerating, setRegenerating] = useState(false)
  const [failure, setFailure] = useState<LifecycleFailure>()
  const conflict =
    isRunVersionConflict(save.error) ||
    isRunVersionConflict(refresh.error) ||
    isRunVersionConflict(failure?.error)
  const failureMessage =
    failure === undefined || isRunVersionConflict(failure.error)
      ? undefined
      : failure.error instanceof RunGenerationError
        ? failure.error.message
        : failure.fallback

  const onRefresh = async () => {
    if (run.status === 'final' && !window.confirm(t.refresh.confirmFinal)) return
    setRefresh({ busy: true })
    try {
      const { body, diff } = await buildRefresh({
        definition: template,
        views,
        run,
        load: async (query) => toRows(await cubeApi.load(query)),
      })
      await refreshRun.mutateAsync({ ...body, expected_version: run.version })
      setRefresh({ busy: false, diff })
    } catch (error) {
      setRefresh({ busy: false, error })
    }
  }
  const refreshMessage =
    refresh.error instanceof RunGenerationError
      ? refresh.error.message
      : isRunVersionConflict(refresh.error)
        ? undefined
        : refresh.error
          ? t.refresh.failed
          : undefined

  const onSave = () => {
    setSavedVersion(undefined)
    save.mutate(
      { content: getDocumentJSON(editor), expected_version: run.version },
      {
        onSuccess: (updated) => {
          setSaved(snapshot)
          setSavedVersion(updated.version)
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {leaveGuard.dialog}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 w-fit"
            render={<Link to="/belgeler" />}
            nativeButton={false}
          >
            <ArrowLeft />
            {editorText.back}
          </Button>
          <h1 className="text-lg font-semibold">
            {template.title} · {periodLabel(run.period)}
          </h1>
          <p className="text-muted-foreground flex flex-wrap items-center gap-x-3 text-sm">
            <span>{t.status[run.status]}</span>
            <span>{t.dataAt(formatDateTime(run.refreshed_at ?? run.created_at))}</span>
            {!editable && <span>{t.readOnly}</span>}
            <span>
              {editable ? t.edit.hint : run.status === 'final' ? t.edit.final : t.edit.noPermission}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          {/* Baskı sunucudaki kayıtlı kopyayı basar; kaydedilmemiş metin varsa kapalı. */}
          <Button
            variant="outline"
            disabled={dirty}
            render={<Link to={`/yazdir/belge/${run.report_id}/${run.id}`} />}
            nativeButton={false}
          >
            <Printer />
            {t.print.action}
          </Button>
          {/* Sunucu da kayıtlı kopyayı basar; kaydedilmemiş metin varsa kapalı. */}
          <RunPdfButton reportId={run.report_id} runId={run.id} disabled={dirty} />
          <RunLifecycleActions
            run={run}
            canEdit={canEdit}
            dirty={dirty}
            regenerating={regenerating}
            onRegenerate={() => {
              setFailure(undefined)
              setRegenerating(true)
            }}
            onFail={setFailure}
          />
          {canEdit && (
            <Button variant="outline" onClick={() => void onRefresh()} disabled={refresh.busy}>
              <RefreshCw />
              {refresh.busy ? t.refresh.running : t.refresh.action}
            </Button>
          )}
          {editable && (
            <>
              {dirty && <span className="text-muted-foreground text-sm">{editorText.unsaved}</span>}
              {!dirty && savedVersion !== undefined && (
                <span role="status" className="text-positive text-sm">
                  {editorText.saved(savedVersion)}
                </span>
              )}
              <Button onClick={onSave} disabled={save.isPending || !dirty}>
                <Save />
                {save.isPending ? editorText.saving : editorText.save}
              </Button>
            </>
          )}
        </div>
      </div>

      {regenerating && (
        <RunRegenerateForm
          run={run}
          template={template}
          views={views}
          onFail={setFailure}
          onClose={() => setRegenerating(false)}
          onDone={onReload}
        />
      )}
      {failureMessage && (
        <p role="alert" className="text-destructive text-sm">
          {failureMessage}
        </p>
      )}
      {refresh.diff && <RunRefreshNotice diff={refresh.diff} />}
      {refreshMessage && (
        <p role="alert" className="text-destructive text-sm">
          {refreshMessage}
        </p>
      )}

      {conflict && (
        <Alert
          tone="danger"
          title={t.edit.conflict}
          action={
            <Button size="sm" variant="outline" onClick={onReload}>
              {editorText.reload}
            </Button>
          }
        />
      )}
      {save.isError && !conflict && <ErrorState error={save.error} />}
      {editable && (
        <EditorToolbar editor={editor}>
          <RunBlockActions editor={editor} />
        </EditorToolbar>
      )}

      <FrozenDocumentSource
        config={{
          views,
          queries: template.queries,
          filters: template.filters,
          filterValues: run.filters,
          period: run.period,
          data: run.data,
        }}
      >
        <div
          aria-label={editorText.pageLabel}
          className={cn(
            'border-border bg-card text-card-foreground mx-auto min-h-96 w-full rounded-md border p-8',
            template.orientation === 'landscape' ? 'max-w-[297mm]' : 'max-w-[210mm]',
          )}
        >
          <Letterhead
            title={template.title}
            period={run.period}
            createdAt={run.created_at}
            refreshedAt={run.refreshed_at}
          />
          <EditorContent editor={editor} className={DOCUMENT_CLASS} />
        </div>
      </FrozenDocumentSource>
    </div>
  )
}

/**
 * Dönem kopyası (`/belgeler/:id/:runId`). Rakamlar `run.data`'dan çizilir, Cube'a sorgu gitmez;
 * Cube meta'sı (oturumda zaten yüklü, aynı önbellek) yalnız alan biçimi içindir. `draft` kopyada
 * yalnız metin düzenlenir (widget ekleme/ayar yok; blok silme/taşıma var); `final` salt okunur.
 * Şablon tanımı güncel sürümden okunur (sorgu adı -> view eşlemesi).
 */
export function RunPage() {
  const { id, runId } = useParams()
  const queryClient = useQueryClient()
  const run = useRun(id, runId)
  const report = useBiReport(id)
  const meta = useCubeMeta()
  // Editörü baştan kurmak için anahtar: "yeniden yükle" sonrası.
  const [reloadKey, setReloadKey] = useState(0)

  const failure = run.error ?? report.error ?? meta.error
  if (failure) return <ErrorState error={failure} />
  if (!run.data || !report.data || !meta.data) return <Loading rows={8} />

  const template = reportDocumentSchema.safeParse(report.data.definition)
  if (!template.success) {
    return (
      <p role="alert" className="text-destructive text-sm">
        {editorText.invalidDefinition}
      </p>
    )
  }
  const views = new Map(
    meta.data.cubes.filter((cube) => cube.type === 'view').map((cube) => [cube.name, cube]),
  )
  const current = run.data
  return (
    <RunDocument
      key={`${current.id}:${reloadKey}`}
      run={current}
      template={template.data}
      views={views}
      canEdit={report.data.can_edit}
      onReload={() =>
        void queryClient
          .invalidateQueries({
            queryKey: queryKeys.bi.run(current.report_id, current.id),
            exact: true,
          })
          .then(() => setReloadKey((key) => key + 1))
      }
    />
  )
}
